/** 1. BROCKETT — yellow legal-pad skin. Free: cuisines you love, avoid/dietary, "my speed" (price),
 * teams. Supporter: voice dials (1-5) + Brockett's Notebook (auto-observations + your notes). */
import { useState } from 'react';
import type { Preferences, Profile } from '@pghbites/shared';
import { MAX_NOTEBOOK_NOTES } from '@pghbites/shared';
import { Chips } from './Chips.js';
import { ExampleBlock } from './ExampleBlock.js';
import {
  CUISINE_OPTIONS, DIETARY_OPTIONS, PRICE_OPTIONS, TEAM_OPTIONS, VOICE_DIALS,
  EXAMPLE_DIALS, EXAMPLE_OBSERVATIONS, EXAMPLE_NOTES,
} from './options.js';

type ArrayField = 'fav_cuisines' | 'dietary_avoidances' | 'sports_teams';

interface BrockettProps {
  profile: Profile;
  preferences: Preferences;
  isSupporter: boolean;
  onToggleArray: (field: ArrayField, value: string) => void;
  onSetPrice: (value: string) => void;
  onSetDial: (key: string, value: number) => void;
  onAddNote: (text: string) => void;
  onRemoveNote: (id: string) => void;
  onRemoveObservation: (id: string) => void;
}

function Dials({ values, onSet, readOnly }: { values: Record<string, number>; onSet?: (k: string, v: number) => void; readOnly?: boolean }) {
  return (
    <>
      {VOICE_DIALS.map((d) => {
        const v = values[d.key] ?? 3;
        return (
          <div className="dial" key={d.key}>
            <span className="dlabel">{d.label}</span>
            {readOnly ? (
              <span className="bar"><i style={{ width: `${(v / 5) * 100}%` }} /></span>
            ) : (
              <input
                type="range" min={1} max={5} step={1} value={v}
                aria-label={`${d.label} (1-5)`}
                onChange={(e) => onSet?.(d.key, Number(e.target.value))}
              />
            )}
            <span className="dval">{v}</span>
          </div>
        );
      })}
    </>
  );
}

export function BrockettWindow({
  profile, preferences, isSupporter, onToggleArray, onSetPrice, onSetDial, onAddNote, onRemoveNote, onRemoveObservation,
}: BrockettProps) {
  const [noteDraft, setNoteDraft] = useState('');
  const atCap = preferences.notebook_notes.length >= MAX_NOTEBOOK_NOTES;

  const submitNote = () => {
    const t = noteDraft.trim();
    if (!t) return;
    onAddNote(t);
    setNoteDraft('');
  };

  return (
    <div className="sec">
      <div className="seclabel"><span className="dot">📒</span> Brockett keeps your food notes</div>
      <div className="pad">
        <div className="tape" />
        <div className="holes"><i /><i /><i /><i /><i /></div>
        <div className="hd"><span className="t">Brockett&rsquo;s pad</span></div>

        <div className="lbl">cuisines you love</div>
        <Chips options={CUISINE_OPTIONS} selected={profile.fav_cuisines} ariaLabel="cuisines you love"
          onToggle={(v) => onToggleArray('fav_cuisines', v)} />

        <div className="lbl">avoid &middot; dietary</div>
        <Chips options={DIETARY_OPTIONS} selected={profile.dietary_avoidances} ariaLabel="avoid or dietary"
          onToggle={(v) => onToggleArray('dietary_avoidances', v)} />

        <div className="lbl">my speed</div>
        <Chips options={PRICE_OPTIONS} selected={profile.price_range ? [profile.price_range] : []} single ariaLabel="my speed (price)"
          onToggle={(v) => onSetPrice(v === profile.price_range ? '' : v)} />

        <div className="lbl">teams</div>
        <Chips options={TEAM_OPTIONS} selected={profile.sports_teams} ariaLabel="teams"
          onToggle={(v) => onToggleArray('sports_teams', v)} />

        {isSupporter ? (
          <>
            <div className="lbl">his personality, your way</div>
            <Dials values={preferences as unknown as Record<string, number>} onSet={onSetDial} />

            <div className="notebook">
              <h4>what Brockett remembers</h4>
              <ul>
                {preferences.notebook_observations.length === 0 && <li>Nothing yet — he&rsquo;ll learn as you go.</li>}
                {preferences.notebook_observations.map((o) => (
                  <li key={o.id}>
                    <span>{o.text}</span>
                    <button type="button" className="x" aria-label={`Forget: ${o.text}`} onClick={() => onRemoveObservation(o.id)}>×</button>
                  </li>
                ))}
              </ul>
              <div className="divider" />
              <h4>your notes for Brockett</h4>
              <ul>
                {preferences.notebook_notes.map((n) => (
                  <li key={n.id}>
                    <span>{n.text}</span>
                    <button type="button" className="x" aria-label={`Remove: ${n.text}`} onClick={() => onRemoveNote(n.id)}>×</button>
                  </li>
                ))}
              </ul>
              {!atCap && (
                <div className="addrow">
                  <input
                    type="text" value={noteDraft} placeholder="add a note for Brockett…"
                    aria-label="add a note for Brockett"
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }}
                  />
                  <button type="button" onClick={submitNote}>add</button>
                </div>
              )}
            </div>
          </>
        ) : (
          <ExampleBlock hint={<>🔒 This is a peek. <b>Become a Supporter</b> to give Brockett his own personality and a notebook that remembers you.</>}>
            <div className="lbl">his personality, your way</div>
            <Dials values={EXAMPLE_DIALS} readOnly />
            <div className="notebook">
              <h4>what Brockett remembers</h4>
              <ul>{EXAMPLE_OBSERVATIONS.map((o) => <li key={o.id}><span>{o.text}</span><span className="x">×</span></li>)}</ul>
              <div className="divider" />
              <h4>your notes for Brockett</h4>
              <ul>{EXAMPLE_NOTES.map((n) => <li key={n.id}><span>{n.text}</span><span className="x">×</span></li>)}</ul>
            </div>
          </ExampleBlock>
        )}
      </div>
      <div className="hint">tap a chip to toggle →</div>
    </div>
  );
}
